import { useState, useCallback } from 'react';
import type { TourStep } from '@/components/ui/product-tour';

const TOUR_STORAGE_KEY = 'tasks-tour-completed';

export function useTasksTour() {
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [hasCompletedTour, setHasCompletedTour] = useState(() => {
    return localStorage.getItem(TOUR_STORAGE_KEY) === 'true';
  });

  const startTour = useCallback(() => {
    setIsTourOpen(true);
  }, []);

  const closeTour = useCallback(() => {
    setIsTourOpen(false);
  }, []);

  const completeTour = useCallback(() => {
    localStorage.setItem(TOUR_STORAGE_KEY, 'true');
    setHasCompletedTour(true);
    setIsTourOpen(false);
  }, []);

  const resetTour = useCallback(() => {
    localStorage.removeItem(TOUR_STORAGE_KEY);
    setHasCompletedTour(false);
  }, []);

  const tourSteps: TourStep[] = [
    {
      target: '[data-tour="tasks-header"]',
      title: 'Welcome to Tasks',
      content: 'This is your central hub for managing and tracking tasks across your organization. Create, assign, and monitor tasks to keep your team organized and productive.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="view-mode-selector"]',
      title: 'View Modes',
      content: 'Switch between different views: List for detailed task information, Board for kanban-style organization, or Calendar to see tasks by due date.',
      placement: 'left',
    },
    {
      target: '[data-tour="new-task-button"]',
      title: 'Create New Task',
      content: 'Click here to create a new task. You can assign it to team members, set priorities, due dates, and add detailed descriptions.',
      placement: 'left',
    },
    {
      target: '[data-tour="search-tasks"]',
      title: 'Search Tasks',
      content: 'Quickly find tasks by searching through titles, descriptions, or assignee names. The search updates in real-time as you type.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="quick-filters"]',
      title: 'Quick Filters',
      content: 'Use these quick filters to instantly view specific work item groups like "My Work Items", "Overdue", "Due Today", or "Urgent" items. Click any filter to activate it.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="tasks-list"]',
      title: 'Tasks List',
      content: 'View all your tasks here. Select tasks using the checkboxes to perform bulk actions. Click on any task to view its full details and make updates.',
      placement: 'top',
    },
  ];

  return {
    isTourOpen,
    hasCompletedTour,
    tourSteps,
    startTour,
    closeTour,
    completeTour,
    resetTour,
  };
}
